<template>
  <div class="pa-10 suspicious-ips-list">
    <title-page icon="mdi-shield-alert-outline">
      {{ $t('security.suspicious-ips.title') }}
    </title-page>

    <v-card class="mt-4">
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" md="6">
            <v-btn-toggle v-model="blockingFilter" mandatory dense color="primary">
              <v-btn small :value="'all'">{{ $t('security.suspicious-ips.filter-all') }}</v-btn>
              <v-btn small :value="'blocking'">{{ $t('security.suspicious-ips.filter-blocking') }}</v-btn>
              <v-btn small :value="'history'">{{ $t('security.suspicious-ips.filter-history') }}</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
                :label="$t('security.suspicious-ips.search-ip')"
                v-model="searchIp"
                prepend-inner-icon="mdi-magnify"
                clearable
                hide-details
                dense
                outlined
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-divider class="my-6" />

    <v-data-table
        :headers="headers"
        :items="filteredItems"
        :options.sync="options"
        :loading="loading"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
        dense
    >
      <template #item.ip="{item}">
        <span class="font-weight-medium">{{ item.ip }}</span>
      </template>

      <template #item.isCurrentlyBlocked="{item}">
        <v-chip x-small :color="item.isCurrentlyBlocked ? 'error' : 'grey'" text-color="white">
          <v-icon left x-small>{{ item.isCurrentlyBlocked ? 'mdi-shield-off-outline' : 'mdi-shield-check-outline' }}</v-icon>
          {{ item.isCurrentlyBlocked ? $t('security.suspicious-ips.status-blocked') : $t('security.suspicious-ips.status-flagged') }}
        </v-chip>
      </template>

      <template #item.escalationCount="{item}">
        <v-chip x-small :color="escalationColor(item.escalationCount)" text-color="white">
          {{ item.escalationCount }}
        </v-chip>
      </template>

      <template #item.lastDistinctIdentifierCount="{value}">
        {{ value != null ? value : '-' }}
      </template>

      <template #item.lastReason="{item}">
        <span v-if="item.lastReason" class="text-caption">{{ reasonLabel(item.lastReason) }}</span>
        <span v-else>-</span>
      </template>

      <template #item.firstFlaggedAt="{value}">
        {{ value ? formatDate(value, $t('format_datetime')) : '-' }}
      </template>

      <template #item.lastFlaggedAt="{value}">
        {{ value ? formatDate(value, $t('format_datetime')) : '-' }}
      </template>

      <template #item.blockedUntil="{value}">
        <span v-if="value">{{ formatDate(value, $t('format_datetime')) }}</span>
        <span v-else class="text--secondary">-</span>
      </template>

      <template #item.actions="{item}">
        <v-tooltip bottom v-if="item.isCurrentlyBlocked && canUnblock">
          <template #activator="{on, attrs}">
            <v-btn
                icon
                small
                color="primary"
                :loading="!!unblocking[item.ip]"
                v-on="on"
                v-bind="attrs"
                @click="unblock(item)"
            >
              <v-icon small>mdi-lock-open-variant-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('security.suspicious-ips.unblock') }}</span>
        </v-tooltip>
        <span v-else-if="!item.isCurrentlyBlocked" class="text--secondary">-</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import SecurityRepository from "@repository/SecurityRepository"

export default {
  name: 'SuspiciousIpsList',

  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      searchIp: '',
      blockingFilter: 'blocking',
      unblocking: {},
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: [],
        sortDesc: [],
      },
    }
  },

  computed: {
    canUnblock() {
      return !!(this.currentUser && this.currentUser.permissions && this.currentUser.permissions.includes('USER_WRITE'))
    },

    headers() {
      return [
        { text: this.$t('security.suspicious-ips.col-ip'), value: 'ip', sortable: false, width: 180 },
        { text: this.$t('security.suspicious-ips.col-status'), value: 'isCurrentlyBlocked', sortable: false, width: 140 },
        { text: this.$t('security.suspicious-ips.col-escalation'), value: 'escalationCount', sortable: false, width: 110 },
        { text: this.$t('security.suspicious-ips.col-distinct-identifiers'), value: 'lastDistinctIdentifierCount', sortable: false, width: 130 },
        { text: this.$t('security.suspicious-ips.col-last-reason'), value: 'lastReason', sortable: false },
        { text: this.$t('security.suspicious-ips.col-last-flagged'), value: 'lastFlaggedAt', sortable: false, width: 180 },
        { text: this.$t('security.suspicious-ips.col-blocked-until'), value: 'blockedUntil', sortable: false, width: 180 },
        { text: '', value: 'actions', sortable: false, width: 60 },
      ]
    },

    filteredItems() {
      const q = (this.searchIp || '').trim().toLowerCase()
      if (!q) return this.items
      return this.items.filter(o => (o.ip || '').toLowerCase().includes(q))
    },
  },

  watch: {
    blockingFilter() { this.resetAndLoad() },
    options: { handler() { this.loadData() }, deep: true },
  },

  mounted() {
    this.loadData()
  },

  methods: {
    resetAndLoad() {
      if (this.options.page > 1) {
        this.options.page = 1
      } else {
        this.loadData()
      }
    },

    loadData() {
      const params = {
        page: this.options.page,
        limit: this.options.itemsPerPage,
      }
      if (this.blockingFilter === 'blocking') {
        params.blockingOnly = true
      } else if (this.blockingFilter === 'history') {
        params.blockingOnly = false
      }

      this.loading = true
      SecurityRepository.listSuspiciousIps(params).then(result => {
        this.items = result.items || []
        this.total = result.total || 0
        this.loading = false
      }).catch(err => {
        this.loading = false
        this.notifyError(err)
      })
    },

    unblock(item) {
      if (!item || !item.ip) return
      this.$set(this.unblocking, item.ip, true)
      SecurityRepository.unblockSuspiciousIp(item.ip).then(() => {
        this.$set(this.unblocking, item.ip, false)
        this.notifySuccess('security.suspicious-ips.unblock-success')
        this.loadData()
      }).catch(err => {
        this.$set(this.unblocking, item.ip, false)
        this.notifyError(err)
      })
    },

    escalationColor(count) {
      if (count >= 5) return 'error'
      if (count >= 3) return 'orange darken-2'
      if (count >= 2) return 'amber darken-2'
      return 'grey darken-1'
    },

    reasonLabel(key) {
      const k = 'security.suspicious-ips.reasons.' + key
      return this.$te(k) ? this.$t(k) : key
    },
  },
}
</script>
